/**
 * 品题工作台请求错误：保留服务端 details，便于面板「查看错误详情」。
 */
export class TtqWorkbenchRequestError extends Error {
  readonly errorCode?: string;
  readonly details?: unknown;
  readonly statusCode?: number;

  constructor(
    message: string,
    options?: {
      errorCode?: string;
      details?: unknown;
      statusCode?: number;
    },
  ) {
    super(message);
    this.name = 'TtqWorkbenchRequestError';
    this.errorCode = options?.errorCode;
    this.details = options?.details;
    this.statusCode = options?.statusCode;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function truncate(text: string, max = 4000): string {
  const raw = String(text || '');
  return raw.length > max ? `${raw.slice(0, max)}…` : raw;
}

/** FC 常把 content_preview / hint 放在 details 或再包一层 details.details */
function resolveDetailRecord(details: unknown): Record<string, unknown> | null {
  if (!isRecord(details)) return null;
  const hasPreviewFields = (row: Record<string, unknown>) => (
    typeof row.content_preview === 'string'
    || typeof row.hint === 'string'
    || typeof row.parse_error === 'string'
  );
  if (hasPreviewFields(details)) return details;
  if (isRecord(details.details) && hasPreviewFields(details.details)) {
    return details.details;
  }
  return details;
}

function readErrorMeta(error: unknown): {
  message: string;
  details: unknown;
  errorCode?: string;
  statusCode?: number;
} {
  const message = error instanceof Error ? error.message : String(error || '未知错误');
  const bag = (error && typeof error === 'object')
    ? error as {
      details?: unknown;
      errorCode?: string;
      payload?: unknown;
      statusCode?: number;
    }
    : {};
  return {
    message,
    details: error instanceof TtqWorkbenchRequestError
      ? error.details
      : (bag.details ?? bag.payload),
    errorCode: error instanceof TtqWorkbenchRequestError
      ? error.errorCode
      : bag.errorCode,
    statusCode: error instanceof TtqWorkbenchRequestError
      ? error.statusCode
      : bag.statusCode,
  };
}

/** 变式方向预览空结果 / 422：短暂抖动时可自动再试一次 */
export function isTtqDirectionPreviewRetryable(error: unknown): boolean {
  const { message, details, errorCode, statusCode } = readErrorMeta(error);
  const detailRecord = resolveDetailRecord(details);
  const code = String(
    errorCode
    || (detailRecord && typeof detailRecord.error === 'string' ? detailRecord.error : '')
    || '',
  ).trim();
  if (code === 'direction_preview_empty') return true;
  if (statusCode === 422 && /预览结果为空|direction_preview_empty/i.test(message)) return true;
  if (/预览结果为空|direction_preview_empty/i.test(message)) return true;
  if (detailRecord && typeof detailRecord.message === 'string'
    && /预览结果为空/i.test(detailRecord.message)) {
    return true;
  }
  return false;
}

/** 从任意抛错提取可展示的摘要 + 调试详情 */
export function formatTtqWorkbenchError(error: unknown): {
  summary: string;
  detail: string;
} {
  const { message, details, errorCode, statusCode } = readErrorMeta(error);

  const detailRecord = resolveDetailRecord(details);
  const nestedPreview = detailRecord && typeof detailRecord.content_preview === 'string'
    ? detailRecord.content_preview
    : null;
  const nestedHint = detailRecord && typeof detailRecord.hint === 'string'
    ? detailRecord.hint
    : null;
  const nestedParse = detailRecord && typeof detailRecord.parse_error === 'string'
    ? detailRecord.parse_error
    : (detailRecord && typeof detailRecord.message === 'string' ? detailRecord.message : null);
  const code = String(
    errorCode
    || (detailRecord && typeof detailRecord.error === 'string' ? detailRecord.error : '')
    || '',
  ).trim();

  const isPreviewEmpty = code === 'direction_preview_empty'
    || /预览结果为空|direction_preview_empty/i.test(message)
    || (typeof nestedParse === 'string' && /direction_preview_empty|预览结果为空/i.test(nestedParse));

  const isParseFail = code === 'ttq_json_parse_failed'
    || code === 'ttq_assess_json_parse_failed'
    || /格式无法解析|未生成有效题目结构|ttq_json_parse|ttq_assess_json_parse/i.test(message);

  let summary: string;
  if (isPreviewEmpty) {
    summary = nestedHint?.trim()
      || '变式方向预览为空（HTTP 422 / direction_preview_empty）。模型未返回可用方向说明，可更换模型或再次点击预览重试。';
  } else if (isParseFail) {
    summary = '生成失败：模型返回内容无法整理成有效题目。可更换模型或调整说明后重试。';
  } else {
    summary = message;
  }

  const parts: string[] = [
    `用户可见摘要：${summary}`,
    `原始错误：${message}`,
  ];
  if (typeof statusCode === 'number') {
    parts.push(`HTTP：${statusCode}`);
  }
  if (code) parts.push(`错误码：${code}`);
  if (nestedHint) parts.push(`提示：${nestedHint}`);
  if (nestedParse && nestedParse !== message) parts.push(`解析说明：${nestedParse}`);
  if (nestedPreview) parts.push(`模型原文预览：\n${truncate(nestedPreview, 1600)}`);
  if (detailRecord?.debug) {
    parts.push(`结构调试：\n${truncate(JSON.stringify(detailRecord.debug, null, 2), 800)}`);
  }
  if (details !== undefined) {
    try {
      parts.push(`原始 details：\n${truncate(JSON.stringify(details, null, 2), 3500)}`);
    } catch {
      parts.push(`原始 details：${String(details)}`);
    }
  }

  return { summary, detail: parts.join('\n\n') };
}
